import { FC, useCallback, useMemo } from 'react'
import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import cn from 'classnames'
import type { Field } from '@/api/types/crm/crm.types'
import { useAddAlert } from '@/hooks/useAddAlert'
import {
  MAX_ACCOUNT_FIELD_UNIQUE_NAME,
  TELEGRAM_ACCOUNT_FIELD_UNIQUE_NAME,
} from '@pages/Admin/CRM/utils/messengerAccountFields'
import {
  pickMaxAccountField,
  pickTelegramAccountField,
} from '@pages/Admin/CRM/utils/mainProfileFields'
import { isCrmLeadIntegrationField } from '@pages/Admin/CRM/utils/crmLeadIntegrationFields'
import LeadField from '../../../components/LeadField/LeadField'

import classes from './index.module.css'

type MessengerAccountLinkFieldUniqueName =
  | typeof TELEGRAM_ACCOUNT_FIELD_UNIQUE_NAME
  | typeof MAX_ACCOUNT_FIELD_UNIQUE_NAME

const FIELD_CONFIG: Record<
  MessengerAccountLinkFieldUniqueName,
  {
    label: string
    tooltip: string
    pickField: (fields: Field[]) => Field | undefined
  }
> = {
  [TELEGRAM_ACCOUNT_FIELD_UNIQUE_NAME]: {
    label: 'Ссылка на Telegram',
    tooltip: 'Формат: https://t.me/username или числовой id',
    pickField: pickTelegramAccountField,
  },
  [MAX_ACCOUNT_FIELD_UNIQUE_NAME]: {
    label: 'Ссылка на MAX',
    tooltip: 'Формат: https://max.ru/u/уникальный-код или числовой id',
    pickField: pickMaxAccountField,
  },
}

type MessengerAccountLinkFieldProps = {
  uniqueName: MessengerAccountLinkFieldUniqueName
  fields: Field[]
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  integrationConnected: boolean
  integrationLoading?: boolean
  error?: string
  className?: string
}

const MessengerAccountLinkField: FC<MessengerAccountLinkFieldProps> = ({
  uniqueName,
  fields,
  value,
  onChange,
  disabled = false,
  integrationConnected,
  integrationLoading = false,
  error,
  className,
}) => {
  const addAlert = useAddAlert()
  const config = FIELD_CONFIG[uniqueName]

  const catalogField = useMemo(
    () => config.pickField(fields),
    [config, fields],
  )

  const isVisible = useMemo(
    () =>
      Boolean(catalogField) &&
      !isCrmLeadIntegrationField(catalogField) &&
      !integrationLoading &&
      integrationConnected,
    [catalogField, integrationConnected, integrationLoading],
  )

  const trimmedValue = value.trim()

  const handleCopy = useCallback(async () => {
    if (!trimmedValue) return

    try {
      await navigator.clipboard.writeText(trimmedValue)
      addAlert({
        title: 'Успешно',
        text: 'Ссылка скопирована в буфер обмена',
        type: 'success',
      })
    } catch {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось скопировать ссылку',
        type: 'error',
      })
    }
  }, [addAlert, trimmedValue])

  if (!isVisible || !catalogField) return null

  return (
    <div className={cn(className)}>
      <div className={classes.block}>
        <div className={classes.header}>
          <span className={classes.label}>{config.label}</span>
          <Tooltip
            placement="top"
            title={config.tooltip}
            overlayClassName={classes.tooltipOverlay}
          >
            <InfoCircleOutlined
              className={classes.infoIcon}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </Tooltip>
        </div>
        <div className={classes.field}>
          <div
            className={cn(classes.fieldControl, {
              [classes.fieldControlError]: Boolean(error),
            })}
          >
            <div className={classes.fieldInput}>
              <LeadField
                field={catalogField}
                size="small"
                value={value}
                onChange={onChange}
                disabled={disabled}
              />
            </div>
            {trimmedValue ? (
              <Tooltip title="Скопировать ссылку" placement="top">
                <button
                  type="button"
                  className={classes.copyButton}
                  aria-label={`Скопировать ${config.label.toLowerCase()}`}
                  onClick={() => void handleCopy()}
                >
                  <CopyOutlined
                    className={classes.copyIcon}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </button>
              </Tooltip>
            ) : null}
          </div>
          {error ? (
            <span className={cn(classes.status, classes.statusError)}>{error}</span>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default MessengerAccountLinkField
